import React, { useState, useRef } from "react";
import {
  View,
  StyleSheet,
  Modal,
  Pressable,
  Dimensions,
  type GestureResponderEvent
} from "react-native";
import { colors, layout, shadows } from "@/constants";
import Interactive from "./Interactive";
import Icon, { IconName } from "./Icon";
import Text from "./Text";

/**
 * A single menu item for the context menu
 */
export interface ContextMenuItem {
  /** Icon to display */
  icon: IconName;
  /** Label text */
  label: string;
  /** Callback when pressed */
  onPress: () => void;
  /** Styling variant — "danger" renders in red */
  variant?: "danger";
}

/**
 * Props for the ItemContextMenu component
 */
interface ItemContextMenuProps {
  /** Menu items to display */
  items: ContextMenuItem[];
}

/**
 * Reusable "..." context menu that shows a popover with action items
 * Used for session and fact cards to provide delete actions
 */
export default function ItemContextMenu({ items }: ItemContextMenuProps) {
  const [visible, setVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef<View>(null);

  const handleOpen = (event: GestureResponderEvent) => {
    // Stop propagation so a parent Interactive (e.g., a list row) doesn't
    // also fire when the user taps the menu trigger.
    event.stopPropagation();
    buttonRef.current?.measureInWindow((x, y, width, height) => {
      const { width: screenWidth, height: screenHeight } =
        Dimensions.get("window");
      const estimatedMenuHeight =
        layout.space.xs * 2 + items.length * (layout.space.sm * 2 + 20);
      const spaceBelow = screenHeight - (y + height);
      const openAbove = spaceBelow < estimatedMenuHeight + layout.space.xs;

      setMenuPosition({
        top: openAbove
          ? y - estimatedMenuHeight - layout.space.xs
          : y + height + layout.space.xs,
        right: screenWidth - x - width
      });
      setVisible(true);
    });
  };

  const handleItemPress = (onPress: () => void) => {
    setVisible(false);
    onPress();
  };

  return (
    <View ref={buttonRef}>
      <Interactive
        onPress={handleOpen}
        style={styles.trigger}
        accessibilityLabel="More options"
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <Icon name="more_vert" size="medium" color="tertiary" />
      </Interactive>

      <Modal
        visible={visible}
        transparent
        animationType="fade"
        onRequestClose={() => setVisible(false)}
      >
        <Pressable style={styles.overlay} onPress={() => setVisible(false)}>
          <View
            style={[
              styles.menu,
              { top: menuPosition.top, right: menuPosition.right }
            ]}
          >
            {items.map((item) => (
              <Interactive
                key={item.label}
                style={styles.menuItem}
                onPress={() => handleItemPress(item.onPress)}
                accessibilityLabel={item.label}
              >
                <Icon
                  name={item.icon}
                  size="medium"
                  style={
                    item.variant === "danger"
                      ? { color: colors.error }
                      : undefined
                  }
                />
                <Text
                  style={
                    item.variant === "danger"
                      ? { color: colors.error }
                      : undefined
                  }
                >
                  {item.label}
                </Text>
              </Interactive>
            ))}
          </View>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  trigger: {
    padding: layout.space.xs,
    borderRadius: layout.borderRadius.round
  },
  overlay: {
    flex: 1
  },
  menu: {
    position: "absolute",
    backgroundColor: colors.surface,
    borderRadius: layout.borderRadius.medium,
    paddingVertical: layout.space.xs,
    minWidth: 140,
    ...shadows.raised
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: layout.space.sm,
    paddingVertical: layout.space.sm,
    paddingHorizontal: layout.space.md,
    borderRadius: layout.borderRadius.small,
    marginHorizontal: layout.space.xs
  }
});
